import { DESKTOP_MODE } from '@components/previewUI/utils/constants.js';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import { ANIMATION_TYPES } from '@views/AdminPanelView/components/AdminPanel/components/LinkInBio/components/Design/components/BackgroundType/components/BackgroundAnimation/utils/constants.js';

export const designSlice = createSlice({
  name: 'design',
  initialState: {
    background: {
      type: 1,
      color: '#ffffff',
      image: '',
      animation: '',
      animation3dEmoji: '',
      animation3dPalette: '',
      animation3dPattern: '',
      animation3dTheme: '',
      animationType: ANIMATION_TYPES.gifs,
      video: '',
      videoSound: false,
      blur: 0,
      brightness: 0,
      opacity: 0,
      effect: '',

      isGradient: false,
      gradientColor1: '#ffffff',
      gradientColor2: '#000000',
      gradientDirection: 0,
      gradientOffset: 0,
    },

    desktopMode: DESKTOP_MODE.mobile,
    desktopModeWidth: 0,

    font: 'Open Sans',
    previewBackgroundColor: '#2d2c31',
    previewProfileImage: '',
    pageNavigation: {
      backgroundColor: '#ffffff',
      opacity: 0,
      textColor: '#000000',
      view: 'top',
    },
    tabs: {
      activeBackgroundColor: '#ffffff',
      activeTextColor: '#000000',
      backgroundColor: '#a69c9b',
      textColor: '#ffffff',
      opacity: 0,
      Radius: 0,
      height: 0,
      marginTop: null,
      marginBottom: null,
      view: 'plain',
      isNavigation: true,
      isContent: true,
      isPosts: false,
      isStore: false,
      items: [],
    },
    posts: {
      backgroundColor: '#ffffff',
      textColor: '#000000',
      opacity: 0,
    },
    // Component-level brand colors generated server-side from the 4-5 base
    // colors. previewUI reads these (with safe fallbacks) so Cart / Subscribe /
    // Cta / Social / Heading / Footer stop hard-coding hex values.
    brandColors: {
      cartButtonBackgroundColor: '',
      cartButtonTextColor: '',
      subscribeButtonBackgroundColor: '',
      subscribeButtonTextColor: '',
      ctaButtonBackgroundColor: '',
      ctaButtonTextColor: '',
      socialBackgroundColor: '',
      socialColor: '',
      headingTextColor: '',
      footerBackgroundColor: '',
      footerTextColor: '',
    },
    templateId: 0,
    themeId: 0,
    paletteId: 0,
    storeLayout: 'single',
    themeMode: 'light',
  },
  reducers: {
    updateThemeMode: (state, action) => {
      state.themeMode = action.payload;
    },
    designStore: (state, { payload }) => {
      state.background = {
        type: payload.background.type ?? 1,
        color: payload.background.color ?? '#ffffff',
        image: payload.background.image ?? '',
        animation: payload.background.animation ?? '',
        animation3dEmoji: payload.background.animation3dEmoji ?? '😃',
        animation3dPalette: payload.background.animation3dPalette ?? '',
        animation3dPattern: payload.background.animation3dPattern ?? '',
        animation3dTheme: payload.background.animation3dTheme ?? '',
        animationType: payload.background.animationType ?? ANIMATION_TYPES.gifs,
        video: payload.background.video ?? '',
        videoSound: payload.background.videoSound ?? false,
        blur: payload.background.blur ?? 0,
        brightness: payload.background.brightness ?? 0,
        opacity: payload.background.opacity ?? 0,
        effect: payload.background.effect ?? '',

        isGradient: payload.background.isGradient ?? false,
        gradientColor1: payload.background.gradientColor1 ?? '#ffffff',
        gradientColor2: payload.background.gradientColor2 ?? '#000000',
        gradientDirection: payload.background.gradientDirection ?? 0,
        gradientOffset: payload.background.gradientOffset ?? 0,
      };

      const rawMode = payload.desktopMode ?? DESKTOP_MODE.classic;
      state.desktopMode = rawMode === 'desktop' ? DESKTOP_MODE.social : rawMode === 'mobile' ? DESKTOP_MODE.classic : rawMode;
      state.desktopModeWidth = payload.desktopModeWidth ?? 0;

      state.font = payload.font ?? 'Open Sans';
      state.previewBackgroundColor = payload.previewBackgroundColor ?? '#2d2c31';
      state.previewProfileImage = payload.previewProfileImage ?? '';
      state.pageNavigation = {
        backgroundColor: payload.pageNavigation.backgroundColor ?? '#ffffff',
        opacity: payload.pageNavigation.opacity ?? 0,
        textColor: payload.pageNavigation.textColor ?? '#000000',
        view: payload.pageNavigation.view ?? 'top',
      };
      state.tabs = {
        activeBackgroundColor: payload.tabs.activeBackgroundColor ?? '#ffffff',
        activeTextColor: payload.tabs?.activeTextColor ?? '#000000',
        backgroundColor: payload.tabs.backgroundColor ?? '#a69c9b',
        textColor: payload.tabs.textColor ?? '#ffffff',
        opacity: payload.tabs?.opacity ?? 0,
        Radius: payload.tabs?.radius ?? 0,
        height: payload.tabs?.height ?? 0,
        // null = not set → previewUI keeps legacy spacing (top 6 / bottom 20). Explicit 0 is honored.
        marginTop: payload.tabs?.marginTop ?? null,
        marginBottom: payload.tabs?.marginBottom ?? null,
        view: payload.tabs?.view ?? 'plain',
        isNavigation: payload.tabs?.isNavigation ?? true,
        isContent: payload.tabs?.isContent ?? true,
        isPosts: payload.tabs?.isPosts ?? true,
        isStore: payload.tabs?.isStore ?? true,
        contentN: payload.tabs?.contentN ?? 1,
        postsN: payload.tabs?.postsN ?? 2,
        storeN: payload.tabs?.storeN ?? 3,
        items: payload.tabs.items ?? [],
      };
      state.posts = {
        backgroundColor: payload.posts.backgroundColor ?? '#a69c9b',
        textColor: payload.posts.textColor ?? '#ffffff',
        opacity: payload.posts.opacity ?? 0,
      };
      // Empty string when backend hasn't generated a value yet — previewUI
      // treats falsy as "use my hard-coded default" so legacy profiles stay
      // visually unchanged until the user re-applies brand colors / a template.
      const bc = payload.brandColors ?? {};
      state.brandColors = {
        cartButtonBackgroundColor: bc.cartButtonBackgroundColor ?? '',
        cartButtonTextColor: bc.cartButtonTextColor ?? '',
        subscribeButtonBackgroundColor: bc.subscribeButtonBackgroundColor ?? '',
        subscribeButtonTextColor: bc.subscribeButtonTextColor ?? '',
        ctaButtonBackgroundColor: bc.ctaButtonBackgroundColor ?? '',
        ctaButtonTextColor: bc.ctaButtonTextColor ?? '',
        socialBackgroundColor: bc.socialBackgroundColor ?? '',
        socialColor: bc.socialColor ?? '',
        headingTextColor: bc.headingTextColor ?? '',
        footerBackgroundColor: bc.footerBackgroundColor ?? '',
        footerTextColor: bc.footerTextColor ?? '',
      };
      state.storeLayout = payload.storeLayout ?? 'single';
      state.isHideLogo = payload.isHideLogo ?? false;
    },
    designStoreId: (state, { payload }) => {
      state.templateId = payload.templateId ?? 0;
      state.themeId = payload.themeId ?? 0;
      state.paletteId = payload.paletteId ?? 0;
    },
    updateBackground: (state, action) => {
      state.background = action.payload;
    },
    updateDesktopMode: (state, action) => {
      state.desktopMode = action.payload;
    },
    updateDesktopModeWidth: (state, action) => {
      state.desktopModeWidth = action.payload;
    },
    updateFont: (state, action) => {
      state.font = action.payload;
    },
    updatePreviewBackgroundColor: (state, action) => {
      state.previewBackgroundColor = action.payload;
    },
    updatePreviewProfileImage: (state, action) => {
      state.previewProfileImage = action.payload;
    },
    updatePageNavigation: (state, action) => {
      state.pageNavigation = action.payload;
    },
    updateTabs: (state, action) => {
      state.tabs = action.payload;
    },
    /**
     * Append a newly-created custom tab into tabs.items.
     * Backend returns the tab with either PascalCase (direct DbML serialization) or camelCase.
     * We normalize to the shape used by TabDragDrop/Navigation (camelCase-ish matching predefined tabs).
     */
    addCustomTab: (state, { payload }) => {
      if (!payload) return;
      const normalized = {
        id: payload.id ?? payload.Id,
        type: payload.type ?? payload.Type ?? 'custom',
        blockType: payload.blockType ?? payload.BlockType,
        customKey: payload.customKey ?? payload.CustomKey,
        title: payload.title ?? payload.Title,
        icon: payload.icon ?? payload.Icon,
        description: payload.description ?? payload.Description,
        settings: payload.settings ?? payload.Settings,
        isActive: payload.isActive ?? payload.IsActive ?? true,
        isAdded: payload.isAdded ?? payload.IsAdded ?? true,
        n: payload.n ?? payload.N ?? (state.tabs.items?.length ?? 0) + 1,
      };
      if (!state.tabs.items) state.tabs.items = [];
      state.tabs.items.push(normalized);
    },
    /** Remove a custom tab (by id) from local state. Call after DELETE succeeds. */
    removeCustomTab: (state, { payload: tabId }) => {
      if (!state.tabs.items) return;
      state.tabs.items = state.tabs.items.filter((t) => (t.id ?? t.Id) !== tabId);
    },
    /** Merge updates (rename, icon change, reorder) into an existing custom tab. */
    updateCustomTab: (state, { payload }) => {
      if (!payload) return;
      const targetId = payload.id ?? payload.Id;
      if (!targetId || !state.tabs.items) return;
      state.tabs.items = state.tabs.items.map((t) => {
        const tid = t.id ?? t.Id;
        return tid === targetId ? { ...t, ...payload } : t;
      });
    },
    updatePostsDesign: (state, action) => {
      state.posts = action.payload;
    },
    updateTemplateId: (state, action) => {
      state.templateId = action.payload;
    },
    updateThemeId: (state, action) => {
      state.themeId = action.payload;
    },
    updatePaletteId: (state, action) => {
      state.paletteId = action.payload;
    },
    designUpdateStoreLayout: (state, action) => {
      state.storeLayout = action.payload;
    },
    updateIsHideLogo: (state, action) => {
      state.isHideLogo = action.payload;
    },
  },
});

export const {
  designStore,
  designStoreId,
  updateBackground,
  updateDesktopMode,
  updateDesktopModeWidth,
  updateFont,
  updatePreviewBackgroundColor,
  updatePreviewProfileImage,
  updatePageNavigation,
  updateTabs,
  addCustomTab,
  removeCustomTab,
  updateCustomTab,
  updatePostsDesign,
  updateTemplateId,
  updateThemeId,
  updatePaletteId,
  designUpdateStoreLayout,
  updateIsHideLogo,
  updateThemeMode,
} = designSlice.actions;

export const selectDesign = (state) => state.design;
export const selectBackground = (state) => state.design.background;
export const selectDesktopMode = (state) => state.design.desktopMode;
export const selectDesktopModeWidth = (state) => state.design.desktopModeWidth;
export const selectFont = (state) => state.design.font;
export const selectPreviewBackgroundColor = (state) => state.design.previewBackgroundColor;
export const selectPreviewProfileImage = (state) => state.design.previewProfileImage;
export const selectPageNavigation = (state) => state.design.pageNavigation;
export const selectTabs = (state) => state.design.tabs;
const selectTabsItems = (state) => state.design.tabs?.items;

export const selectCustomTabs = createSelector(selectTabsItems, (items) => (items || []).filter((t) => (t.type ?? t.Type) === 'custom'));

export const selectCustomTabByKey = createSelector([selectTabsItems, (_state, customKey) => customKey], (items, customKey) =>
  (items || []).find((t) => (t.type ?? t.Type) === 'custom' && (t.customKey ?? t.CustomKey) === customKey),
);
export const selectPostsDesign = (state) => state.design.posts;
export const selectTemplateId = (state) => state.design.templateId;
export const selectThemeId = (state) => state.design.themeId;
export const selectPaletteId = (state) => state.design.paletteId;

export const selectStoreLayout = (state) => state.design.storeLayout;
export const selectIsHideLogo = (state) => state.design.isHideLogo;
export const selectBrandColors = (state) => state.design.brandColors;
export const selectThemeMode = (state) => state.design.themeMode;
export default designSlice.reducer;
